import type { ApiResult } from './types';

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH';
  body?: unknown;
  authToken?: string;
}

function buildHeaders(authToken?: string): HeadersInit {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
    headers['x-cog-id'] = authToken;
  }
  return headers;
}

function extractErrorMessage(data: unknown, statusText: string): string {
  if (typeof data === 'string' && data.trim()) {
    return data.trim();
  }

  if (typeof data === 'object' && data !== null) {
    if (
      'message' in data &&
      typeof (data as { message: unknown }).message === 'string'
    ) {
      return (data as { message: string }).message;
    }

    if (
      'error' in data &&
      typeof (data as { error: unknown }).error === 'string'
    ) {
      return (data as { error: string }).error;
    }
  }

  return statusText || 'Request failed';
}

function logApiError(
  method: string,
  url: string,
  statusCode: number | undefined,
  data: unknown,
  errorMessage: string
): void {
  console.error('[finpay-vob-webform] API error', {
    method,
    url,
    statusCode,
    message: errorMessage,
    body: data,
  });
}

export async function apiRequest<T = unknown>(
  apiBase: string,
  path: string,
  options: RequestOptions = {}
): Promise<ApiResult<T>> {
  const result: ApiResult<T> = { hasErrors: false };
  const method = options.method ?? 'GET';
  const url = `${apiBase.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;

  try {
    const response = await fetch(url, {
      method,
      headers: buildHeaders(options.authToken),
      body:
        options.body !== undefined ? JSON.stringify(options.body) : undefined,
    });

    const contentType = response.headers.get('Content-Type') ?? '';
    const data = contentType.includes('application/json')
      ? await response.json()
      : await response.text();

    result.statusCode = response.status;

    if (!response.ok) {
      result.hasErrors = true;
      result.entity = data as T;
      result.errorMessage = extractErrorMessage(data, response.statusText);
      logApiError(method, url, response.status, data, result.errorMessage);
      return result;
    }

    result.entity = data as T;
    return result;
  } catch (error) {
    result.hasErrors = true;
    result.errorMessage =
      error instanceof Error ? error.message : 'Network request failed';
    logApiError(method, url, undefined, undefined, result.errorMessage);
    return result;
  }
}
